import { Col, Row, Tooltip } from "antd";
import dayjs from "dayjs";
import { get, set } from "lodash";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdOutlineBadge,
  MdOutlineCake,
  MdOutlineCheckCircle,
  MdOutlineHealthAndSafety,
  MdOutlineQrCode,
} from "react-icons/md";
import Alert from "shared/components/Alert";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import {
  DATE_FORMAT,
  GENDER,
  RELATIONSHIP,
  SOCIAL_REGIMES,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";

const Children = ({
  setPercent,
  isFieldRequired,
  bindField,
  initialValues,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });
  const getAge = (birthdate) => {
    if (!birthdate) return 0;
    const today = new Date();
    const birth = new Date(birthdate);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const children = get(initialValues, "children", []);

  const [teletransmission, setTeletransmission] = useState(
    () => !get(children, "[0].identity.fields.teletransmission", true),
  );

  const handleTeletransmissionChange = useCallback(
    (value) => {
      setTeletransmission(value);
      children.forEach((_, index) => {
        set(
          initialValues,
          `children.${index}.identity.fields.teletransmission`,
          !value,
        );
      });
      setPercent(initialValues);
    },
    [children, initialValues, setPercent],
  );

  return (
    get(initialValues, "project.contact.relatedPersons", []).find(
      (person) => person.relationship === RELATIONSHIP.CHILD,
    ) && (
      <div
        className="subscription-form--wrapper"
        id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.CHILDREN}-section`}
      >
        <Form
          type="vertical"
          title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CHILDREN.TITLE}
          subtitle={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CHILDREN.SUBTITLE}
        >
          <Row gutter={[16, 16]}>
            <Col lg={24} xs={24}>
              <Alert
                showIcon
                type="info"
                message={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CHILDREN.INFO}
              />
            </Col>
            {children.map((_, index) => {
              const age = getAge(_.identity?.user?.birthDate);
              const isMajor = age > 18;

              const lastnamePath = `children.${index}.identity.user.lastname`;
              const lastnameField = bindField(lastnamePath, {
                label: SUBSCRIPTION.FORM.LAST_NAME.LABEL,
                required: isFieldRequired(lastnamePath),
                onBlur: () => setPercent(initialValues),
              });

              const firstnamePath = `children.${index}.identity.user.firstname`;
              const firstnameField = bindField(firstnamePath, {
                label: SUBSCRIPTION.FORM.FIRST_NAME.LABEL,
                required: isFieldRequired(firstnamePath),
                onBlur: () => setPercent(initialValues),
              });

              const genderPath = `children.${index}.identity.user.gender`;
              const genderField = bindField(genderPath, {
                label: SUBSCRIPTION.FORM.CHILD_GENDER.LABEL,
                required: isFieldRequired(genderPath),
                onBlur: () => setPercent(initialValues),
              });

              const socialRegimePath = `children.${index}.identity.socialRegime`;
              const socialRegimeField = bindField(socialRegimePath, {
                label: SUBSCRIPTION.FORM.MANDATORY_SCHEME.LABEL,
                required: isFieldRequired(socialRegimePath),
                disabled: true,
              });

              const teletransmissionPath = `children.${index}.identity.fields.teletransmission`;
              const teletransmissionField = bindField(teletransmissionPath, {
                label: SUBSCRIPTION.FORM.TELETRANSMISSION.LABEL,
                onChange: handleTeletransmissionChange,
              });

              const refundOtherAccountPath = `children.${index}.identity.fields.refundOtherAccount`;
              const refundOtherAccountField = bindField(refundOtherAccountPath, {
                label: SUBSCRIPTION.FORM.REFUND_OTHER_ACCOUNT.LABEL,
                onChange: () => setPercent(initialValues),
              });

              const ibanPath = `children.${index}.identity.fields.payment.IBAN`;
              const ibanField = bindField(ibanPath, {
                label: SUBSCRIPTION.FORM.IBAN.LABEL,
                required: isFieldRequired(ibanPath),
                onBlur: () => setPercent(initialValues),
              });

              const bicPath = `children.${index}.identity.fields.payment.BIC`;
              const bicField = bindField(bicPath, {
                label: SUBSCRIPTION.FORM.BIC.LABEL,
                required: isFieldRequired(bicPath),
                onBlur: () => setPercent(initialValues),
              });

              return (
                <Col lg={24} xs={24} key={`child-form-${index}`}>
                  <Form
                    type="highlight"
                    title={`${
                      SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CHILDREN.CHILD
                        .TITLE
                    } ${index + 1}`}
                  >
                    <Row gutter={[16, 16]}>
                      <Col lg={24} xs={24}>
                        <FormField
                          path={lastnameField.path}
                          label={lastnameField.label}
                          required={lastnameField.required}
                          disabled={lastnameField.disabled}
                        >
                          {(field) => (
                            <Input
                              label={lastnameField.label}
                              icon={<MdOutlineBadge size={16} />}
                              placeholder={
                                SUBSCRIPTION.FORM.LAST_NAME.PLACEHOLDER
                              }
                              type="custom"
                              normalizeText={true}
                              defaultValue={lastnameField.defaultValue}
                              {...field}
                              onChange={lastnameField.onChange}
                              onBlur={lastnameField.onBlur}
                            />
                          )}
                        </FormField>
                      </Col>
                      <Col lg={24} xs={24}>
                        <FormField
                          path={firstnameField.path}
                          label={firstnameField.label}
                          required={firstnameField.required}
                          disabled={firstnameField.disabled}
                        >
                          {(field) => (
                            <Input
                              placeholder={
                                SUBSCRIPTION.FORM.FIRST_NAME.PLACEHOLDER
                              }
                              label={firstnameField.label}
                              icon={<MdOutlineBadge size={16} />}
                              type="custom"
                              normalizeText={true}
                              defaultValue={firstnameField.defaultValue}
                              {...field}
                              onChange={firstnameField.onChange}
                              onBlur={firstnameField.onBlur}
                            />
                          )}
                        </FormField>
                      </Col>
                      <Col lg={24} xs={24}>
                        <FormField
                          path={genderField.path}
                          label={genderField.label}
                          required={genderField.required}
                          disabled={genderField.disabled}
                        >
                          {(field) => (
                            <Input
                              type="radio"
                              variant="group"
                              label={genderField.label}
                              values={[
                                {
                                  label:
                                    SUBSCRIPTION.FORM.CHILD_GENDER.OPTIONS
                                      .MALE,
                                  value: GENDER.MALE,
                                },
                                {
                                  label:
                                    SUBSCRIPTION.FORM.CHILD_GENDER.OPTIONS
                                      .FEMALE,
                                  value: GENDER.FEMALE,
                                },
                              ]}
                              icon={<MdOutlineBadge size={22} />}
                              value={get(initialValues, genderPath)}
                              {...field}
                              onChange={genderField.onChange}
                              onBlur={genderField.onBlur}
                              optional={!genderField.required}
                            />
                          )}
                        </FormField>
                      </Col>
                      <Col lg={24} xs={24}>
                        <FormField
                          path={socialRegimeField.path}
                          label={socialRegimeField.label}
                          required={socialRegimeField.required}
                          disabled={socialRegimeField.disabled}
                        >
                          {(field) => (
                            <Select
                              label={socialRegimeField.label}
                              icon={<MdOutlineHealthAndSafety size={16} />}
                              placeholder={
                                SUBSCRIPTION.FORM.MANDATORY_SCHEME.PLACEHOLDER
                              }
                              type="simple"
                              options={Object.entries(SOCIAL_REGIMES).map(
                                ([key, value]) => ({
                                  value,
                                  label:
                                    SUBSCRIPTION.FORM.MANDATORY_SCHEME
                                      .MUTUAL_MOBILITY_OPTIONS[key],
                                }),
                              )}
                              defaultValue={socialRegimeField.defaultValue}
                              {...field}
                              onChange={socialRegimeField.onChange}
                              onBlur={socialRegimeField.onBlur}
                            />
                          )}
                        </FormField>
                      </Col>
                      <Col lg={24} xs={24}>
                        <Input
                          label={SUBSCRIPTION.FORM.BIRTH_DATE.LABEL}
                          icon={<MdOutlineCake size={16} />}
                          placeholder={SUBSCRIPTION.FORM.BIRTH_DATE.PLACEHOLDER}
                          type="date"
                          variant="simple"
                          defaultValue={dayjs(
                            get(
                              initialValues,
                              `children.${index}.identity.user.birthDate`,
                            ),
                          )}
                          format={DATE_FORMAT}
                          disabled
                          required={isFieldRequired(
                            `children.${index}.identity.user.birthDate`,
                          )}
                        />
                      </Col>
                      <Col lg={24} xs={24}>
                        <Tooltip
                          title={SUBSCRIPTION.FORM.TELETRANSMISSION.SYNC_NOTICE}
                        >
                          <div>
                            <FormField
                              path={teletransmissionField.path}
                              label={teletransmissionField.label}
                              required={teletransmissionField.required}
                              disabled={teletransmissionField.disabled}
                            >
                              {(field) => (
                                <Input
                                  type="switch"
                                  label={teletransmissionField.label}
                                  icon={<MdOutlineCheckCircle size={16} />}
                                  checked={teletransmission}
                                  {...field}
                                  onChange={teletransmissionField.onChange}
                                  onBlur={teletransmissionField.onBlur}
                                />
                              )}
                            </FormField>
                          </div>
                        </Tooltip>
                      </Col>
                      {isMajor && (
                        <>
                          <Col lg={24} xs={24}>
                            <FormField
                              path={refundOtherAccountField.path}
                              label={refundOtherAccountField.label}
                              required={refundOtherAccountField.required}
                              disabled={refundOtherAccountField.disabled}
                            >
                              {(field) => (
                                <Input
                                  type="switch"
                                  label={refundOtherAccountField.label}
                                  icon={<MdOutlineCheckCircle size={16} />}
                                  checked={
                                    get(
                                      initialValues,
                                      refundOtherAccountPath,
                                    ) === true
                                  }
                                  {...field}
                                  onChange={refundOtherAccountField.onChange}
                                  onBlur={refundOtherAccountField.onBlur}
                                />
                              )}
                            </FormField>
                          </Col>
                          {get(initialValues, refundOtherAccountPath) && (
                            <>
                              <Col lg={24} xs={24}>
                                <FormField
                                  path={ibanField.path}
                                  label={ibanField.label}
                                  required={ibanField.required}
                                  disabled={ibanField.disabled}
                                >
                                  {(field) => (
                                    <Input
                                      label={ibanField.label}
                                      icon={<MdOutlineQrCode size={16} />}
                                      type="custom"
                                      stripSpaces={true}
                                      uppercase={true}
                                      placeholder={
                                        SUBSCRIPTION.FORM.IBAN.PLACEHOLDER
                                      }
                                      defaultValue={ibanField.defaultValue}
                                      {...field}
                                      onChange={ibanField.onChange}
                                      onBlur={ibanField.onBlur}
                                    />
                                  )}
                                </FormField>
                              </Col>
                              <Col lg={24} xs={24}>
                                <FormField
                                  path={bicField.path}
                                  label={bicField.label}
                                  required={bicField.required}
                                  disabled={bicField.disabled}
                                >
                                  {(field) => (
                                    <Input
                                      label={bicField.label}
                                      icon={<MdOutlineQrCode size={16} />}
                                      type="custom"
                                      stripSpaces={true}
                                      placeholder={
                                        SUBSCRIPTION.FORM.BIC.PLACEHOLDER
                                      }
                                      defaultValue={bicField.defaultValue}
                                      {...field}
                                      onChange={bicField.onChange}
                                      onBlur={bicField.onBlur}
                                    />
                                  )}
                                </FormField>
                              </Col>
                            </>
                          )}
                        </>
                      )}
                    </Row>
                  </Form>
                </Col>
              );
            })}
          </Row>
        </Form>
      </div>
    )
  );
};

export default Children;
